/**
 * Admin page for managing integration configurations.
 * Allows administrators to configure OAuth credentials for integrations.
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import IntegrationAdminService, {
  IntegrationConfig,
  SaveIntegrationRequest,
} from '../services/IntegrationAdminService';

const PROVIDER_ICONS: Record<string, string> = {
  google_business: 'G',
  datev: 'D',
};

const PROVIDER_COLORS: Record<string, string> = {
  google_business: '#4285F4',
  datev: '#005AA0',
};

interface EditModalProps {
  integration: IntegrationConfig;
  onClose: () => void;
  onSave: (provider: string, data: SaveIntegrationRequest) => Promise<void>;
  saving: boolean;
}

function EditModal({ integration, onClose, onSave, saving }: EditModalProps): React.JSX.Element {
  const [clientId, setClientId] = useState(integration.client_id || '');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState(integration.redirect_uri || '');
  const [isActive, setIsActive] = useState(integration.is_active);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientId.trim()) {
      toast.error('Client ID is required');
      return;
    }

    if (!integration.has_client_secret && !clientSecret.trim()) {
      toast.error('Client Secret is required for new configurations');
      return;
    }

    await onSave(integration.provider, {
      client_id: clientId.trim(),
      client_secret: clientSecret.trim() || undefined,
      redirect_uri: redirectUri.trim(),
      is_active: isActive,
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: '28px 32px',
          minWidth: 500,
          maxWidth: 600,
          boxShadow: '0 8px 32px rgba(30,40,90,0.18)',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          data-testid="integration-modal-close"
          style={{
            position: 'absolute',
            top: 12,
            right: 16,
            background: 'none',
            border: 'none',
            fontSize: 22,
            color: '#888',
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>

        <h2
          style={{
            margin: 0,
            marginBottom: 8,
            fontWeight: 600,
            color: '#002b80',
            fontSize: '1.25rem',
          }}
        >
          Configure {integration.name}
        </h2>

        {integration.source === 'env' && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: '#fff3cd',
              borderRadius: 8,
              fontSize: '0.9rem',
              color: '#856404',
            }}
          >
            This integration is currently configured via environment variables.
            Saving here will override with database configuration.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 6,
                fontWeight: 500,
                color: '#333',
                fontSize: '0.95rem',
              }}
            >
              Client ID *
            </label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Enter OAuth Client ID"
              data-testid="integration-client-id-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: '0.95rem',
                boxSizing: 'border-box',
              }}
              disabled={saving}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 6,
                fontWeight: 500,
                color: '#333',
                fontSize: '0.95rem',
              }}
            >
              Client Secret {integration.has_client_secret ? '(leave blank to keep existing)' : '*'}
            </label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={
                integration.has_client_secret
                  ? 'Enter new secret to change'
                  : 'Enter OAuth Client Secret'
              }
              data-testid="integration-client-secret-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: '0.95rem',
                boxSizing: 'border-box',
              }}
              disabled={saving}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 6,
                fontWeight: 500,
                color: '#333',
                fontSize: '0.95rem',
              }}
            >
              Redirect URI
            </label>
            <input
              type="text"
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              placeholder="https://your-domain.com/api/integrations/..."
              data-testid="integration-redirect-uri-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: '0.95rem',
                boxSizing: 'border-box',
              }}
              disabled={saving}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={saving}
                data-testid="integration-is-active-checkbox"
              />
              <span style={{ fontWeight: 500, color: '#333' }}>Enable integration</span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="integration-modal-cancel"
              style={{
                background: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: 6,
                padding: '10px 20px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="integration-modal-save"
              style={{
                background: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 20px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function IntegrationAdminPage(): React.JSX.Element {
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<IntegrationConfig | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, []);

  async function loadIntegrations(): Promise<void> {
    try {
      setLoading(true);
      setError(null);
      const data = await IntegrationAdminService.listIntegrations();
      setIntegrations(data);
    } catch (err: any) {
      console.error('Failed to load integrations:', err);
      setError(err.response?.data?.detail || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(provider: string, data: SaveIntegrationRequest): Promise<void> {
    try {
      setSaving(true);
      await IntegrationAdminService.saveIntegration(provider, data);
      toast.success('Integration configuration saved successfully');
      setEditingIntegration(null);
      await loadIntegrations();
    } catch (err: any) {
      console.error('Failed to save integration:', err);
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(integration: IntegrationConfig): Promise<void> {
    if (!integration.configured) {
      toast.error('Configure the integration first before toggling');
      return;
    }

    try {
      await IntegrationAdminService.toggleIntegration(integration.provider, !integration.is_active);
      toast.success(
        `Integration ${!integration.is_active ? 'enabled' : 'disabled'} successfully`
      );
      await loadIntegrations();
    } catch (err: any) {
      console.error('Failed to toggle integration:', err);
      toast.error(err.response?.data?.detail || 'Failed to toggle integration');
    }
  }

  async function handleDelete(integration: IntegrationConfig): Promise<void> {
    if (integration.source !== 'database') {
      toast.info('This integration uses environment variables and cannot be deleted from here');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the ${integration.name} configuration?`)) {
      return;
    }

    try {
      const response = await IntegrationAdminService.deleteIntegration(integration.provider);
      toast.success(response.message);
      await loadIntegrations();
    } catch (err: any) {
      console.error('Failed to delete integration:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete configuration');
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
        Loading integrations...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <p style={{ color: '#dc3545', marginBottom: 16 }}>{error}</p>
        <button
          onClick={loadIntegrations}
          style={{
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 32, background: '#f8fafc', minHeight: '100vh' }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 8,
          padding: 32,
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              margin: 0,
              color: '#002b80',
              fontWeight: 700,
              fontSize: '1.5rem',
            }}
          >
            Integration Settings
          </h1>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '0.95rem' }}>
            Configure OAuth credentials for third-party integrations. These settings apply globally
            to all tenants.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {integrations.map((integration) => (
            <div
              key={integration.provider}
              data-testid={`integration-card-${integration.provider}`}
              style={{
                border: '1px solid #e0e3ef',
                borderRadius: 12,
                padding: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  background: PROVIDER_COLORS[integration.provider] || '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 24,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {PROVIDER_ICONS[integration.provider] || integration.name.charAt(0)}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#0f2b5a',
                    }}
                  >
                    {integration.name}
                  </h3>

                  {integration.configured && (
                    <span
                      style={{
                        padding: '3px 10px',
                        background: integration.is_active ? '#e8f5e9' : '#f5f5f5',
                        color: integration.is_active ? '#2e7d32' : '#666',
                        borderRadius: 12,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {integration.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}

                  {integration.source && (
                    <span
                      style={{
                        padding: '3px 10px',
                        background: integration.source === 'env' ? '#e3f2fd' : '#fff3cd',
                        color: integration.source === 'env' ? '#1976d2' : '#856404',
                        borderRadius: 12,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }}
                    >
                      {integration.source === 'env' ? 'Environment' : 'Database'}
                    </span>
                  )}
                </div>

                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  {integration.configured ? (
                    <>
                      Client ID: {integration.client_id.substring(0, 20)}
                      {integration.client_id.length > 20 ? '...' : ''}
                      {integration.redirect_uri && (
                        <span style={{ marginLeft: 16 }}>
                          Redirect: {integration.redirect_uri.substring(0, 40)}
                          {integration.redirect_uri.length > 40 ? '...' : ''}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontStyle: 'italic' }}>Not configured</span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {integration.configured && integration.source === 'database' && (
                  <button
                    onClick={() => handleToggle(integration)}
                    data-testid={`integration-toggle-${integration.provider}`}
                    style={{
                      background: integration.is_active ? '#fff3cd' : '#e8f5e9',
                      color: integration.is_active ? '#856404' : '#2e7d32',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                    }}
                  >
                    {integration.is_active ? 'Disable' : 'Enable'}
                  </button>
                )}

                <button
                  onClick={() => setEditingIntegration(integration)}
                  data-testid={`integration-configure-${integration.provider}`}
                  style={{
                    background: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                  }}
                >
                  {integration.configured ? 'Edit' : 'Configure'}
                </button>

                {integration.source === 'database' && (
                  <button
                    onClick={() => handleDelete(integration)}
                    data-testid={`integration-delete-${integration.provider}`}
                    style={{
                      background: '#fff',
                      color: '#dc3545',
                      border: '1px solid #dc3545',
                      borderRadius: 6,
                      padding: '8px 14px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}

          {integrations.length === 0 && (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: '#666',
                background: '#f8f9fa',
                borderRadius: 8,
              }}
            >
              No integrations available.
            </div>
          )}
        </div>
      </div>

      {editingIntegration && (
        <EditModal
          integration={editingIntegration}
          onClose={() => setEditingIntegration(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

export default IntegrationAdminPage;
